/**
 * DirectorVouchPanel - a director vouches for a client's ID on the matter.
 *
 * BM-H3306 (Vanessa, 30 Sep 2026): "vouching whereby a director can vouch
 * for someone's ID. it is built but has disappeared. we sometimes need it
 * during file opening".
 *
 * The COLP/COFA vouching register still lived in the Directors Lounge, but
 * nothing on the matter page reached it, and a vouch recorded there never
 * discharged the AML gate, so a vouched client still sat at CDD. The only
 * in-flow control was in LinkObModal.jsx, removed as dead code in #631.
 *
 * This panel sits under the AML / Thirdfort section on the matter page. It
 * shows only to directors (the server checks again) and only while AML is
 * not yet satisfied, or to say who vouched once they have. It drives
 * POST /vouching/matter/{ref}/vouch, which records the vouch in the same
 * register and discharges the gate. A Thirdfort report that was referred
 * or rejected is not offered a vouch: the server refuses it as well.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserCheck, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const DECLINED = ["referred", "rejected"];
const today = () => new Date().toISOString().split("T")[0];
const FIELD = "w-full text-xs bg-[rgba(13,20,36,0.8)] border border-navy-border rounded p-2 text-white";

export default function DirectorVouchPanel({ matterRef, apiBase = API, onAmlSatisfied }) {
    const [isDirector, setIsDirector] = useState(false);
    const [ob, setOb] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState("");
    const [docsSeen, setDocsSeen] = useState("");
    const [relationship, setRelationship] = useState("");
    const [approvalDate, setApprovalDate] = useState(today());

    const load = useCallback(async () => {
        if (!matterRef) return;
        try {
            const res = await axios.get(`${apiBase}/onboarding/status/${encodeURIComponent(matterRef)}`);
            setOb(res.data || null);
        } catch {
            setOb(null);
        }
    }, [matterRef, apiBase]);

    useEffect(() => {
        let cancelled = false;
        const userId = sessionStorage.getItem("bonsai_user_id") || "";
        axios
            .get(`${apiBase}/user-role`, { headers: { "X-Bonsai-User-Id": userId } })
            .then((res) => { if (!cancelled) setIsDirector(Boolean(res.data?.is_director)); })
            .catch(() => { if (!cancelled) setIsDirector(false); });
        load();
        return () => { cancelled = true; };
    }, [apiBase, load]);

    if (!isDirector || !ob) return null;

    if (ob.aml_confirmed_via === "director_vouch") {
        return (
            <section className="mb-5 p-3 rounded-md border border-navy-border text-xs text-txt-muted" data-testid="director-vouch-done">
                <UserCheck className="inline w-4 h-4 mr-1 text-teal" />
                ID vouched by {ob.aml_confirmed_by} on{" "}
                {ob.aml_confirmed_at ? new Date(ob.aml_confirmed_at).toLocaleDateString("en-GB") : "unknown date"}.
            </section>
        );
    }

    if (ob.aml_satisfied) return null;
    if (DECLINED.includes((ob.thirdfort_report_outcome || "").toLowerCase())) return null;

    const submit = async () => {
        if (reason.trim().length < 10) {
            toast.error("Give a reason of at least 10 characters.");
            return;
        }
        setBusy(true);
        try {
            const res = await axios.post(
                `${apiBase}/vouching/matter/${encodeURIComponent(matterRef)}/vouch`,
                {
                    reason: reason.trim(),
                    id_documents_seen: docsSeen.trim(),
                    relationship_to_client: relationship.trim(),
                    approval_date: approvalDate,
                },
            );
            toast.success(res.data?.message || "ID vouched.");
            setOpen(false);
            await load();
            if (onAmlSatisfied) onAmlSatisfied();
        } catch (e) {
            toast.error(errorMessage(e, "Could not record the vouch."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="mb-5 p-4 rounded-md border border-navy-border" data-testid="director-vouch-panel">
            {!open ? (
                <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="text-xs" data-testid="director-vouch-open-btn">
                    <UserCheck className="w-3.5 h-3.5 mr-1" /> Vouch for client ID (director)
                </Button>
            ) : (
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold flex items-center gap-1.5">
                        <UserCheck className="w-4 h-4 text-teal" /> Director vouching for client ID
                    </h3>
                    <p className="text-[11px] text-status-amber flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Record why standard verification was not possible and what you relied on instead.
                        This clears the AML / CDD step for this matter.
                    </p>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                        placeholder="Reason (required)" className={FIELD} data-testid="director-vouch-reason" />
                    <input value={docsSeen} onChange={(e) => setDocsSeen(e.target.value)}
                        placeholder="ID documents seen" className={FIELD} data-testid="director-vouch-docs" />
                    <input value={relationship} onChange={(e) => setRelationship(e.target.value)}
                        placeholder="How you know the client" className={FIELD} data-testid="director-vouch-relationship" />
                    <label className="text-[11px] text-txt-muted block">
                        Approval date
                        <input type="date" value={approvalDate} onChange={(e) => setApprovalDate(e.target.value)}
                            className={`${FIELD} mt-1`} data-testid="director-vouch-date" />
                    </label>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={submit} disabled={busy} className="text-xs" data-testid="director-vouch-submit">
                            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Record vouch
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="text-xs">Cancel</Button>
                    </div>
                </div>
            )}
        </section>
    );
}
